import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import queryString from 'query-string';

import { isEmpty } from 'lodash';

import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import ListItem from '@material-ui/core/ListItem';
import AutoComplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import BobLoadingMask from '../../../../components/BobLoadingMask';
import BobClientSelector from '../../../../components/BobClientSelector';
import BobConfirmButton from '../../../../components/BobConfirmButton';
import BobCancelButton from '../../../../components/BobCancelButton';

import * as ArchitectActions from '../../../../actions/architectActions';
import * as K2ACoreActions from '../../../../actions/k2aCoreActions';

import * as classes from './ManageSystemPage.module.css';
import BobSiteSelector from '../../../../components/BobSiteSelector';

const ValidationSchema = Yup.object().shape({
    client: Yup.string()
        .nullable()
        .required(),
    site: Yup.string()
        .nullable()
        .required(),
    name: Yup.string()
        .required(),
    systemType: Yup.string()
        .required(),
    equipment: Yup.array()
        .nullable()
});

const ManageSystemPage = ({
    activeNavClient,
    activeNavCompany,
    activeNavSite,
    areas,
    createCoreSystem,
    fetchCoreSystemById,
    fetchEquipmentBySiteId,
    fetchEquipmentsBySiteId,
    fetchSystems,
    history,
    linkEquipmentToSystem,
    selectedSystem,
    setCoreSystem,
    setCoreSystems,
    systems,
    systemTypes,
    unlinkEquipmentSystem,
    updateCoreSystem,
    ...props
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
    const [equipmentLoading, setEquipmentLoading] = useState(false);
    const [equipments, setEquipments] = useState([]);
    const [existingEquipment, setExistingEquipment] = useState([]);

    const queryValues = queryString.parse(history.location.search);

    useEffect(() => {
        return () => {
            const { pathname } = history.location;
            let updatedUrl = history.location.search.replace(/[?&]entityType=[^&]+/, '').replace(/^&/, '?');
            updatedUrl = updatedUrl.replace(/[?&]entityId=[^&]+/, '').replace(/^&/, '?');
            setCoreSystem({});
            history.push({ pathname, search: updatedUrl });
        };
    }, []);

    useEffect(() => {
        const init = async () => {
            formik.setFieldValue('name', selectedSystem.name, false);
            formik.setFieldValue('systemType', selectedSystem.systemType ? selectedSystem.systemType.id : '', false);
            formik.setFieldValue('client', selectedSystem.client, false);
            formik.setFieldValue('site', selectedSystem.site, false);

            const equipment = await fetchEquipmentsBySiteId(selectedSystem.site.id, { SystemId: selectedSystem.id });
            setExistingEquipment(equipment);
            formik.setFieldValue('equipment', equipment, false);
        };

        if (!isEmpty(selectedSystem)) init();
    }, [selectedSystem]);

    useEffect(() => {
        const init = async () => {
            const queryValues = queryString.parse(history.location.search);
            if (queryValues.entityId) {
                if (isEmpty(selectedSystem) || selectedSystem.id !== queryValues.entityId) {
                    await fetchCoreSystemById(queryValues.entityId);
                }
            }
        };

        if (activeNavCompany.id) init();
    }, [activeNavCompany]);

    useEffect(() => {
        if (activeNavClient.id && isEmpty(selectedSystem)) formik.setFieldValue('client', activeNavClient || {});
    }, [activeNavClient]);

    useEffect(() => {
        if (activeNavSite.id && isEmpty(selectedSystem)) formik.setFieldValue('site', activeNavSite || {});
    }, [activeNavSite]);

    const handleRoute = () => {
        const path = '/v2/dashboard/core/entities';

        if (history.location.state && history.location.state.returnUrl) {
            history.push(history.location.state.returnUrl);
        } else {
            history.push({ pathname: path, search: history.location.search });
        }
    };

    const formik = useFormik({
        initialValues: {
            client:     !isEmpty(activeNavClient) ? activeNavClient : '',
            site:       !isEmpty(activeNavSite) ? activeNavSite : '',
            name:       '',
            systemType: '',
            equipment:  []
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const body = {
                siteId:       values.site.id,
                name:         values.name,
                systemTypeId: values.systemType,
            };

            const isUpdate = queryValues.entityId;
            setIsLoading(true);
            let isCreated;
            let promises = [];

            try {
                if (isUpdate) {
                    body.id = queryValues.entityId;
                    isCreated = await updateCoreSystem(queryValues.entityId, body);

                    const equipmentToLink = values.equipment.filter(x => !existingEquipment.some(y => y.id === x.id));
                    const equipmentToUnlink = existingEquipment.filter(x => !values.equipment.some(y => y.id === x.id));

                    equipmentToLink.forEach(item => {
                        promises.push(linkEquipmentToSystem(queryValues.entityId, item.id));
                    });

                    equipmentToUnlink.forEach(item => {
                        promises.push(unlinkEquipmentSystem(queryValues.entityId, item.id));
                    });

                } else {
                    isCreated = await createCoreSystem(values.site.id, body);

                    values.equipment.forEach(item => {
                        promises.push(linkEquipmentToSystem(isCreated.id, item.id));
                    });
                }

                setIsLoading(false);

                if (isCreated) {
                    await Promise.all(promises.map(p => p.catch(error => error)));
                    setCoreSystems([], {});
                    handleRoute();
                }
            } catch (error) {
                setIsLoading(false);
            }
        }
    });

    useEffect(() => {
        if (!activeNavCompany.id || !formik.values.site.id) return;
        const delayDebounceFn = setTimeout(async () => {
            setEquipmentLoading(true);
            const list = await fetchEquipmentBySiteId(formik.values.site.id, { limit: 100, searchFilter: equipmentSearchTerm });
            if (list) setEquipments(list);
            setEquipmentLoading(false);
        }, 700);

        return () => clearTimeout(delayDebounceFn);
    }, [equipmentSearchTerm, formik.values.site]);

    const MenuProps = {
        disablePortal:      true,
        getContentAnchorEl: () => null,
        PaperProps:         {
            style: {
                maxHeight: 300
            },
        },
    };

    const handleClientChange = (event, value) => {
        if (value) {
            formik.setFieldValue('client', value, false);
        } else {
            formik.setFieldValue('client', '', false);
        }
        formik.setFieldValue('site', '', false);
        formik.setFieldValue('equipment', [], false);
    };

    const handleSiteChange = async (event, value) => {

        if (value) {
            formik.setFieldValue('site', value, true);
            setEquipmentLoading(true);
            const equipment = await fetchEquipmentBySiteId(value.id);
            setEquipments(equipment);
            setEquipmentLoading(false);
        } else {
            formik.setFieldValue('site', '', false);
        }
        formik.setFieldValue('equipment', [], false);
    };

    const handleEquipmentChange = (event, value) => {
        formik.setFieldValue('equipment', value, true);
    };

    return (
        <div className={classes.ManageClientPage}>
            <div className={classes.ManagePageHeader}>
                <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}>
                    <BobClientSelector
                        disabled={!isEmpty(queryValues.entityId) || !isEmpty(activeNavClient) ? true : false}
                        companyId={activeNavCompany.id}
                        value={formik.values.client}
                        errors={formik.errors.client}
                        touched={formik.touched.client}
                        handleChange={handleClientChange}
                        label="Select Client"
                        required
                    />
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}>
                    <BobSiteSelector
                        companyId={activeNavCompany.id}
                        clientId={formik.values.client}
                        errors={formik.errors.site}
                        touched={formik.touched.site}
                        value={formik.values.site}
                        handleChange={handleSiteChange}
                        label="Select Site"
                        disabled={isEmpty(formik.values.client) || !isEmpty(queryValues.entityId) || !isEmpty(activeNavSite) ? true : false}
                        required
                    />
                </FormControl>
            </div>
            <div className={classes.ManagePageTitle}>
                System Details
            </div>
            <div className={classes.ManagePageBody}>
                <FormControl className={classes.leftInputWrapper}>
                    <TextField
                        id="name"
                        name="name"
                        type="text"
                        value={formik.values.name}
                        label={<span>System Name<span className={classes.required}> *</span></span>}
                        placeholder="Enter"
                        size="small"
                        onChange={formik.handleChange}
                        InputLabelProps={{
                            classes: {
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }
                        }}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }
                        }}
                        variant="outlined"
                        FormHelperTextProps={{
                            classes: {
                                error: classes.error
                            }
                        }}
                        error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                        helperText={Boolean(formik.touched.name) && formik.errors.name}
                    />
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}>
                    <InputLabel
                        htmlFor="systemType"
                        classes={{
                            error:   classes.error,
                            focused: classes.labelFocused,
                            root:    classes.label
                        }}
                        error={Boolean(formik.errors.systemType) && Boolean(formik.touched.systemType)}
                    >
                        {<span>System Type<span className={classes.required}> *</span></span>}
                    </InputLabel>
                    <Select
                        value={formik.values.systemType}
                        id="systemType"
                        MenuProps={MenuProps}
                        onChange={formik.handleChange}
                        name="systemType"
                        classes={{
                            root: classes.input
                        }}
                        inputProps={{
                            onBlur: formik.handleBlur
                        }}
                        error={Boolean(formik.errors.systemType) && Boolean(formik.touched.systemType)}
                    >
                        {
                            systemTypes.map(type =>
                                <MenuItem
                                    key={type.id}
                                    value={type.id}
                                >
                                    {type.name}
                                </MenuItem>
                            )
                        }
                    </Select>
                    {formik.touched.systemType &&
                        formik.errors.systemType && <FormHelperText className={classes.error}>{formik.errors.systemType}</FormHelperText>}
                </FormControl>
            </div>
            <div className={classes.ManagePageCampus}>
                <div className={classes.CampusTitle}>
                    Equipment Details
                </div>
                <FormControl variant="outlined" size="small" className={classes.leftInputWrapper}>
                    <AutoComplete
                        id="equipment"
                        name="equipment"
                        limitTags={2}
                        disableCloseOnSelect
                        options={equipments}
                        getOptionLabel={(option) => option.name || ''}
                        value={equipments.filter(x => formik.values.equipment.some(y => y.id === x.id))}
                        onInputChange={(event, value, reason) => {
                            if (reason === 'input') {
                                setEquipmentSearchTerm(value);
                            }
                        }}
                        disabled={isEmpty(formik.values.site)}
                        onChange={(event, value) => handleEquipmentChange(event, value)}
                        filterOptions={(x) => x}
                        multiple
                        size="small"
                        renderOption={(props, option) => (
                            <ListItem component="div">
                                <Checkbox
                                    style={{ color: 'black', marginRight: 8 }}
                                    checked={formik.values.equipment.map(item => item.id).indexOf(props.id) > -1}
                                />
                                {props.name}
                            </ListItem>
                        )}
                        renderTags={(value, getTagProps) => {
                            const numTags = value.length;

                            return (
                                <Typography className={classes.Item} variant="body2">
                                    {value
                                        .slice(0, 3)
                                        .map((option, _) => option.name)
                                        .join(', ')}

                                    {numTags > 3 && ` +${numTags - 3} more`}
                                </Typography>
                            );
                        }}
                        renderInput={(params) =>
                            <TextField
                                {...params}
                                className={classes.Required}
                                label="Equipment"
                                variant="outlined"
                                error={formik.errors.equipment ? true : false}
                                helperText={formik.errors.equipment ? formik.errors.equipment : null}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <React.Fragment>
                                            {equipmentLoading ? <CircularProgress color="inherit" size={15} /> : null}
                                            {params.InputProps.endAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                            />}
                    />
                </FormControl>
            </div>
            <div className={classes.ManagePageButtons}>
                <BobCancelButton
                    onClick={handleRoute}
                    disabled={formik.isSubmitting}
                    className={classes.CancelButton}
                    label={'Cancel'}
                />
                <BobConfirmButton
                    onClick={formik.handleSubmit}
                    disabled={formik.isSubmitting}
                    className={isLoading ? classes.DisabledButtons : classes.Buttons}
                    isLoading={isLoading}
                    label={'Save'}
                    iconSize={15}
                />
            </div>
            {
                isEmpty(selectedSystem) && queryValues.entityId ?
                    <BobLoadingMask />
                    :
                    null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeNavClient:  state.bob.activeNavClient,
        activeNavCompany: state.bob.activeNavCompany,
        activeNavSite:    state.bob.activeNavSite,
        areas:            state.k2aCore.areas,
        systemTypes:      state.systemTypes.data,
        navClients:       state.bob.navClients,
        navSites:         state.bob.navSites,
        manufacturers:    state.architect.manufacturers,
        selectedSystem:   state.k2aCore.selectedSystem,
        systems:          state.k2aCore.siteSystems
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...K2ACoreActions
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManageSystemPage));
